/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {Gift, GiftDurationType} from '@app/actions/GiftActionCreators';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

interface GiftDurationTextConfig {
	lifetime: string;
	days: (durationQuantity: number) => string;
	weeks: (durationQuantity: number) => string;
	months: (durationQuantity: number) => string;
	years: (durationQuantity: number) => string;
}

export interface GiftDurationPayload {
	duration_type?: GiftDurationType;
	duration_quantity?: number;
	duration_months?: number | null;
}

function mapLegacyDurationMonthsToFields(durationMonths: number): {
	durationType: GiftDurationType;
	durationQuantity: number;
} {
	if (durationMonths !== 0 && durationMonths % 12 === 0) {
		return {
			durationType: 'years',
			durationQuantity: durationMonths / 12,
		};
	}

	return {
		durationType: 'months',
		durationQuantity: durationMonths,
	};
}

function resolveGiftDuration(gift: Gift | GiftDurationPayload): {
	durationType: GiftDurationType;
	durationQuantity: number;
} {
	if (gift.duration_type !== undefined && gift.duration_quantity !== undefined) {
		return {
			durationType: gift.duration_type,
			durationQuantity: gift.duration_quantity,
		};
	}

	if (gift.duration_months === undefined || gift.duration_months === null) {
		throw new Error('Gift duration payload is missing duration fields');
	}

	return mapLegacyDurationMonthsToFields(gift.duration_months);
}

export function formatGiftDurationText(
	durationType: GiftDurationType,
	durationQuantity: number,
	config: GiftDurationTextConfig,
): string {
	if (durationQuantity === 0) {
		return config.lifetime;
	}

	switch (durationType) {
		case 'days':
			return config.days(durationQuantity);
		case 'weeks':
			return config.weeks(durationQuantity);
		case 'months':
			return config.months(durationQuantity);
		case 'years':
			return config.years(durationQuantity);
		default:
			throw new Error('Unsupported gift duration type');
	}
}

export function getPlutoniumDurationConfig(i18n: I18n): GiftDurationTextConfig {
	return {
		lifetime: i18n._(msg`Visionary (Lifetime Plutonium)`),
		days: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1 ? msg`${durationQuantity} Day of Plutonium` : msg`${durationQuantity} Days of Plutonium`,
			),
		weeks: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Week of Plutonium`
					: msg`${durationQuantity} Weeks of Plutonium`,
			),
		months: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Month of Plutonium`
					: msg`${durationQuantity} Months of Plutonium`,
			),
		years: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Year of Plutonium`
					: msg`${durationQuantity} Years of Plutonium`,
			),
	};
}

export function getPremiumDurationConfig(i18n: I18n): GiftDurationTextConfig {
	return {
		lifetime: i18n._(msg`Lifetime Plutonium`),
		days: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1 ? msg`${durationQuantity} Day of Plutonium` : msg`${durationQuantity} Days of Plutonium`,
			),
		weeks: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Week of Plutonium`
					: msg`${durationQuantity} Weeks of Plutonium`,
			),
		months: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Month of Plutonium`
					: msg`${durationQuantity} Months of Plutonium`,
			),
		years: (durationQuantity: number) =>
			i18n._(
				durationQuantity === 1
					? msg`${durationQuantity} Year of Plutonium`
					: msg`${durationQuantity} Years of Plutonium`,
			),
	};
}

export function getGiftDurationText(i18n: I18n, gift: Gift | GiftDurationPayload): string {
	const duration = resolveGiftDuration(gift);
	return formatGiftDurationText(duration.durationType, duration.durationQuantity, getPlutoniumDurationConfig(i18n));
}

export function getPremiumGiftDurationText(i18n: I18n, gift: Gift | GiftDurationPayload): string {
	const duration = resolveGiftDuration(gift);
	return formatGiftDurationText(duration.durationType, duration.durationQuantity, getPremiumDurationConfig(i18n));
}
